import { Component, OnInit, ViewChild, Input, EventEmitter, Output, OnDestroy } from '@angular/core';
import { Params, ActivatedRoute } from '@angular/router';
import { Subscription, Observable, of, combineLatest } from 'rxjs';
import { EupRoutesService } from '@core/eupRoutes.service';
import { RouterInterceptorService } from '@core/routerInterceptor.service';
import { Utils } from '../utils.service';
import { GlobalSettingsService, ContextParams, GlobalSettings } from '@core/globalSettings.service';
import { MessagesService } from '../../messages/messages.service';
import { WindowsApplicationProtocol, CountryCode, SoftwareOptionsForCompany } from '../enums';
import { RxService } from '../../rx/services/rx.service';
import { DownloadLinkModal } from '@shared/downloadLinkModal/downloadLinkModal.component';
import { WindowApplicationLinkData } from '@shared/windowsApplicationLinkData';
import { TranslateService } from '@ngx-translate/core';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { LabExportModal } from '../labExport/labExport.component';
import { StorageType } from '@shared/enums';
import * as AT from '../../shared/actionTypes';
import { EupObserver } from '@core/eupObserver.service';
import { IteroLabService } from '@shared/iteroLab.service';
import { Company } from '@shared/generalInterfaces';
import { AccessibilityService } from '../accessibility.service';
import { map, take, takeUntil, switchMap } from 'rxjs/operators';
import { AccountManagementService } from '../../practiceManagement/services/account-management.service';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { SoftwareOptionsService } from '@core/companySoftwareOptions/service/softwareOptions.service';
import { ShellContextService } from '../../services/shell-context/shell-context.service';
import { BaseDestroyable } from '@core/base-destroyable';
import { LogoutService } from '../../services/authentication/logout.service';
import { OrdersService } from '@shared/orders/orders.service';
import { Order } from '@shared/orders/order';
import { Consts } from '@shared/consts';
import { CookieService } from '@core/cookie.service';

@Component({
	selector: 'eup-lab-sticky-header',
	templateUrl: './labStickyHeader.component.html',
	styleUrls: ['./labStickyHeader.component.scss'],
	providers: [IteroLabService],
})
export class LabStickyHeaderComponent extends BaseDestroyable implements OnInit, OnDestroy {
	public static readonly homeImage = 'home46';
	public static readonly homeLink = 'home';
	public messagesCount = 0;
	private readonly backImage = 'backButton46';
	private rolePath: string;
	readonly storage: Storage;
	@ViewChild(LabExportModal) labExportModal: LabExportModal;
	@Output() public downloadClicked: EventEmitter<any> = new EventEmitter();

	winAppData: WindowApplicationLinkData;
	hideAppButton = true;
	isLogoutOpen = false;
	isLinksMenuOpen = false;
	disableActionButtons = true;
	lastBowId?: number = null;
	lastDetailsId?: number = null;
	faExternalLinkAlt = faExternalLinkAlt;
	orthoCadSetupDownloadUrl: string;
	iTeroSetupDownloadUrl: string;
	orthoCadSetupDownloadUrl_acs: string;
	iTeroSetupDownloadUrl_acs: string;
	isOrthoCase: boolean;
	backIcon = false;

	contextChangedSubscription: Subscription;
	homeLinkClickSubscription: Subscription;
	orderUpdatedEventSubscription: Subscription;
	eupObserverSubscription: Subscription;

	@Input() titleText = '';
	@Input() image = LabStickyHeaderComponent.homeImage;
	@Input() backLink = LabStickyHeaderComponent.homeLink;
	@Input() backToLastPage = null;
	@Input() showAvatarContainer = 'true';
	@Input() showLinksContainer = 'true';
	@Input() showActionButtons = 'false';
	@Input() uponHomeLinkClickCallback: Function;
	@Input() homeLinkClickEvent: EventEmitter<any>;
	@Input() disableSiteNavigation = 'false';
	@Input() isBackLinkAbsoluteUrl = 'false';
	@Input() shouldAddRoleToBackLink = 'true';
	@Input() orderId: number;
	@Input() shortOrderData: Order;
	@Input() disableDownloadScan: boolean;
	@Input() disableiTeroLab: boolean;

	@ViewChild(DownloadLinkModal) downloadLinkModal: DownloadLinkModal;

	modalDisclaimer = '';
	modalMessageTextKey = '';
	modalFooter = false;

	constructor(
		public globalSettingsService: GlobalSettingsService,
		private shellContextService: ShellContextService,
		private utils: Utils,
		private routerInterceptor: RouterInterceptorService,
		private eupRoutesService: EupRoutesService,
		private route: ActivatedRoute,
		private messagesService: MessagesService,
		private rxService: RxService,
		public orderService: OrdersService,
		private translateService: TranslateService,
		private eupObserver: EupObserver,
		private iteroLabService: IteroLabService,
		private accessibilityService: AccessibilityService,
		private logoutService: LogoutService,
		private accountManagementService: AccountManagementService,
		private applicationNavigationService: ApplicationNavigationService,
		private softwareOptionsService: SoftwareOptionsService,
		private cookieService: CookieService
	) {
		super();
		this.orderUpdatedEventSubscription = this.orderService.OrderUpdated.subscribe((order: Order) => {
			this.updateMembersByOrder(order);
		});
	}

	get windowsApplicationText(): string {
		this.route.queryParams.forEach((queryParams: Params) => {
			if (queryParams.winappdata) {
				this.isOrthoCase = this.shortOrderData?.isOrthoOrder ?? queryParams.isOrtho === 'true';
			}
		});

		return this.isOrthoCase ? this.translateService.instant('Lab.OrthoCAD') : this.translateService.instant('Lab.iTeroLab');
	}

	get username(): string {
		return this.settings ? this.settings.username : '';
	}

	get disableSiteNavigationFunc(): boolean {
		return this.disableSiteNavigation === 'true';
	}

	get shouldAddRoleToBackLinkFunc(): boolean {
		return this.shouldAddRoleToBackLink === 'true';
	}

	private get settings(): GlobalSettings {
		return this.globalSettingsService.get();
	}

	toggleLogoutMenu() {
		this.isLogoutOpen = !this.isLogoutOpen;
	}

	hideLogoutMenu() {
		this.isLogoutOpen = false;
	}

	toggleLinksMenu() {
		this.isLinksMenuOpen = !this.isLinksMenuOpen;
	}

	hideLinksMenu() {
		this.isLinksMenuOpen = false;
	}

	isIteroLab() {
		return !this.isOrthoCase;
	}

	onDownloadRequest(event) {
		this.downloadClicked.emit(event);
	}

	ngOnDestroy(): void {
		this.utils.clearObservablesSubscriptions(this);
		this.eupObserverSubscription?.unsubscribe();
		super.ngOnDestroy();
	}

	ngOnInit(): void {
		this.shellContextService.selectedCompanyId$
			.pipe(
				takeUntil(this.componentAlive$),
				switchMap(companyId =>
					combineLatest([
						this.accountManagementService.getCompanyInfo(companyId),
						this.softwareOptionsService.getCompanySoftwareOptionsKeyValuePair(companyId),
					])
				)
			)
			.subscribe(([companyInfo, softwareOptions]) => {
				const isOrtho = this.isOrthoCase ?? this.shortOrderData?.isOrthoOrder ?? false;

				this.hideAppButton =
					(isOrtho && Object.values(softwareOptions).includes(SoftwareOptionsForCompany.cswo_Hide_OrthoCAD_Show_3DM)) ||
					(!isOrtho && companyInfo?.primaryAddressCountryId === CountryCode.China);
			});

		this.isOrthoCase = this.shortOrderData?.isOrthoOrder ?? false;
		this.modalDisclaimer = this.isOrthoCase ? 'Orders.OrthoDisclaimer' : 'Orders.Disclaimer';
		this.modalMessageTextKey = this.isOrthoCase ? 'Orders.OrthoMessageText' : 'Orders.MessageText';
		this.modalFooter = !this.isOrthoCase;
		this.orthoCadSetupDownloadUrl = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl;
		this.iTeroSetupDownloadUrl = this.eupRoutesService.downloads.iTeroSetupDownloadUrl;
		this.orthoCadSetupDownloadUrl_acs = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl_acs;
		this.iTeroSetupDownloadUrl_acs = this.eupRoutesService.downloads.iTeroSetupDownloadUrl_acs;

		this.rolePath = this.globalSettingsService.rolePath();
		if (this.shouldAddRoleToBackLinkFunc) {
			this.backLink = this.rolePath + '/' + this.backLink;
		}
		this.contextChangedSubscription = this.globalSettingsService.contextChanged.subscribe((params: ContextParams) => {
			if (!params.companyId || !params.doctorId) {
				return;
			}
		});

		// as currently supported only 1 company per personnel
		this.redirectIfRequired(this.settings.companies[0]);

		// when this property is set to true - override image and back link
		// according to previous url
		if (this.backToLastPage === 'true') {
			let previousRoute = this.routerInterceptor.previousRoute.split('?')[0].split('%')[0];

			if (previousRoute === '/') {
				previousRoute = previousRoute + this.backLink;
			}
			// if no previous url exists (e.g. user entered url in a new tab)
			// or the previous url is home so back button should link to home page
			// or user was redirected to a page from straight from login
			if (!previousRoute || previousRoute === '/login' || previousRoute.substring(0, previousRoute.indexOf('?')) === '/login') {
				this.setDefaultLinkAndImage();
			} else {
				// a previous url exists and it is not the home page so back button should link
				// to the last page that the user was at
				this.backLink = previousRoute.slice(1);
				this.image = this.backImage;
				this.backIcon = true;
			}
		} else if (this.backToLastPage === 'false') {
			this.setDefaultLinkAndImage();
		}

		this.route.queryParams.forEach((queryParams: Params) => {
			if (queryParams.winappdata) {
				const isOrtho = this.shortOrderData?.isOrthoOrder ?? queryParams.isOrtho === 'true';
				const name = isOrtho
					? this.translateService.instant('Orders.OpenWithOrthoCAD')
					: this.translateService.instant('Orders.OpenWithiTero');

				this.calculateDownloadLink(isOrtho, queryParams.orderId).subscribe(downloadLink => {
					this.isOrthoCase = isOrtho;
					this.winAppData = new WindowApplicationLinkData(
						queryParams.winappdata,
						name,
						downloadLink,
						isOrtho ? WindowsApplicationProtocol.OrthoCad : WindowsApplicationProtocol.IteroLab
					);
				});
			}

			if (this.orderService.orderObservable$ !== undefined) {
				this.orderService.orderObservable$.subscribe((order: Order) => {
					this.updateMembersByOrder(order);
				});
			}
		});

		this.messagesService.getMessagesCount(this.settings.selectedCompanyId).subscribe((res: number) => {
			this.messagesCount = res;
		});

		const observer = {
			next: value => {
				switch (true) {
					case value.action === AT.WEB_VIEWER_COMPONENT_SAVE_ORDER_PROCEED_TO_MODELING:
					case value.action === AT.RESCAN_COMPONENT_ORDER_RESCAN_SUCCESS:
						this.disableDownloadScan = true;
						this.disableiTeroLab = true;
						break;
					default:
						break;
				}
			},
		};

		this.eupObserverSubscription = this.eupObserver.subscribe(observer);
	}

	private redirectIfRequired(company: Company): void {
		if (!company.handlingEupUrl) {
			return;
		}
		if (company.handlingEupUrl.endsWith('/')) {
			company.handlingEupUrl = company.handlingEupUrl.slice(0, -1);
		}

		const myIteroBffUrl = new URL(this.eupRoutesService.serverUrl);
		const companyMyIteroBffUrl = new URL(company.handlingEupUrl);
		const currentPath = this.accessibilityService.getPathName();
		const myiteroComUrl = new URL(`${this.accessibilityService.getLocation().origin}/login?returnUrl=${currentPath}`);

		const allowRedirection = myIteroBffUrl.host !== companyMyIteroBffUrl.host;

		if (allowRedirection) {
			this.logoutService.logout().subscribe(_ => {
				this.cookieService.set(Consts.CookiesNames.SWITCHREGIONURL, `${companyMyIteroBffUrl.origin}/`, Number.MAX_SAFE_INTEGER);
				this.accessibilityService.redirectTo(myiteroComUrl.href);
			});
		}
	}

	private calculateDownloadLink(isOrtho: boolean, orderId: string): Observable<string> {
		const oldLink = isOrtho ? this.orthoCadSetupDownloadUrl : this.iTeroSetupDownloadUrl;
		const newLink = isOrtho ? this.orthoCadSetupDownloadUrl_acs : this.iTeroSetupDownloadUrl_acs;

		if (!orderId || +orderId <= 0) {
			return of(oldLink);
		}

		const typesMap = {
			[StorageType.ACS]: newLink,
			[StorageType.OriginalFMS]: oldLink,
		};

		return this.orderService.getFileStorage(+orderId).pipe(map(storage => typesMap[storage]));
	}

	logout(): void {
		this.logoutService.logout().subscribe();
	}

	updateMembersByOrder(order: Order) {
		if (order && this.showActionButtons) {
			this.disableActionButtons = false;
			this.disableDownloadScan = !order.canExportFile;
			this.disableiTeroLab = !order.canExportFile;
			this.lastBowId = order.lastBowId;
			this.lastDetailsId = order.lastDetailsId;
		}
	}

	onImageLinkClick(): void {
		if (this.uponHomeLinkClickCallback) {
			this.uponHomeLinkClickCallback();
		}
		if (this.backIcon) {
			this.applicationNavigationService.handleBackClicked();
		} else {
			this.applicationNavigationService.reset();
			this.applicationNavigationService.navigateToHomePage();
		}
	}

	onPrintClicked = () => this.rxService.printOrdersRx(this.orderId.toString()).pipe(take(1)).subscribe();

	onOpenWithiTeroLabClick = () => {
		if (this.iteroLabService.isNeedToSendToLab(this.shortOrderData)) {
			this.iteroLabService.prepareOpenApp(this.orderId, this.lastDetailsId).subscribe(url => {
				this.onWindowsAppClick(url);
				this.orderService.updateLabOrderById(
					this.orderId.toString(),
					this.globalSettingsService.get().selectedDoctorId.toString(),
					this.globalSettingsService.get().selectedCompanyId.toString(),
					(order: Order) => {
						// optimistic UI issue
						order.canReturnToDoctor = false;
					}
				);
			});
		} else {
			this.onWindowsAppClick();
		}
	};

	onWindowsAppClick(link: string = null) {
		this.orderService.startProtocol(link ? link : this.winAppData.link, true);
		this.downloadLinkModal.show(this.winAppData.name, this.winAppData.downloadLink);
	}

	onExportClick = (): void => {
		const orderId = this.orderId + '';
		const doctorId = this.globalSettingsService.get().selectedDoctorId + '';
		const companyId = this.globalSettingsService.get().selectedCompanyId + '';

		this.orderService.getLabOrder({ orderId, doctorId, companyId }).subscribe(order => {
			this.labExportModal.show([order]);
		});
	};

	private setDefaultLinkAndImage() {
		this.backLink = this.rolePath + '/' + LabStickyHeaderComponent.homeLink;
		this.image = LabStickyHeaderComponent.homeImage;
	}
}
